import {
  AutotractorFeature,
  Domain,
  EditorBaseProps,
  EditorPropsType,
} from "common/components/map/layers/types";
import { Feature, Geometry, LineString } from "geojson";
import { FixType } from "protos/geo/geo";

export type EditorFeatureProperties =
  | PointFeatureProperties
  | IndexedPointFeatureProperties
  | DraftPointFeatureProperties
  | GhostPointFeatureProperties
  | PlannedPathFeatureProperties;
export type PlannedPathFeature = Feature<
  Geometry,
  PlannedPathFeatureProperties
>;
export type IndexedPointFeature = Feature<
  Geometry,
  IndexedPointFeatureProperties
>;
export type PointFeature = Feature<Geometry, PointFeatureProperties>;
export type DraftPointFeature = Feature<Geometry, DraftPointFeatureProperties>;
export type GhostPointFeature = Feature<Geometry, GhostPointFeatureProperties>;
export type EditorFeature =
  | PointFeature
  | IndexedPointFeature
  | DraftPointFeature
  | PlannedPathFeature
  | GhostPointFeature;

export const getPlannedPathFeature = (
  f?: AutotractorFeature
): PlannedPathFeature | undefined => {
  if (
    !f ||
    f.properties.domain !== Domain.EDITOR ||
    f.properties.type !== EditorPropsType.PLANNED_PATH
  ) {
    return undefined;
  }
  return {
    ...f,
    properties: {
      ...f.properties,
      // https://github.com/mapbox/mapbox-gl-js/issues/2434
      plannedPath: parseMapBoxFlattenedObject<LineString>(
        f.properties.plannedPath
      ),
    },
  };
};

export const getIndexedPointFeature = (
  f?: AutotractorFeature
): IndexedPointFeature | undefined => {
  if (
    !f ||
    f.properties.domain !== Domain.EDITOR ||
    f.properties.type !== EditorPropsType.INDEXED_POINT
  ) {
    return undefined;
  }
  return {
    ...f,
    properties: {
      ...f.properties,
    },
  };
};

export const getPointFeature = (
  f?: AutotractorFeature
): PointFeature | undefined => {
  if (
    !f ||
    f.properties.domain !== Domain.EDITOR ||
    f.properties.type !== EditorPropsType.POINT
  ) {
    return undefined;
  }
  return {
    ...f,
    properties: {
      ...f.properties,
    },
  };
};

export const getDraftPointFeature = (
  f?: AutotractorFeature
): DraftPointFeature | undefined => {
  if (
    !f ||
    f.properties.domain !== Domain.EDITOR ||
    f.properties.type !== EditorPropsType.DRAFT_POINT
  ) {
    return undefined;
  }
  return {
    ...f,
    properties: {
      ...f.properties,
    },
  };
};

export const getGhostPointFeature = (
  f?: AutotractorFeature
): GhostPointFeature | undefined => {
  if (
    !f ||
    f.properties.domain !== Domain.EDITOR ||
    f.properties.type !== EditorPropsType.GHOST_POINT
  ) {
    return undefined;
  }
  return {
    ...f,
    properties: {
      ...f.properties,
    },
  };
};

const parseMapBoxFlattenedObject = <T>(obj: any): T =>
  JSON.parse(obj as unknown as string);

export interface PlannedPathFeatureProperties extends EditorBaseProps {
  type: EditorPropsType.PLANNED_PATH;
  plannedPath: LineString;
}
export interface GhostPointFeatureProperties extends EditorBaseProps {
  type: EditorPropsType.GHOST_POINT;
}
export interface PointFeatureProperties extends EditorBaseProps {
  type: EditorPropsType.POINT;
}
export interface IndexedPointFeatureProperties extends EditorBaseProps {
  type: EditorPropsType.INDEXED_POINT;
  index: number;
}
/** an existing point being edited in place, e.g. a farm boundary vertex */
export interface DraftPointFeatureProperties extends EditorBaseProps {
  type: EditorPropsType.DRAFT_POINT;
  pointId: string;
  ordinal?: number;
  fixType: FixType;
}

export const PLANNED_PATH_BASE_PROPS: Omit<
  PlannedPathFeatureProperties,
  "plannedPath"
> = {
  __portalFeatureProps: true,
  domain: Domain.EDITOR,
  type: EditorPropsType.PLANNED_PATH,
};

export const GHOST_POINT_BASE_PROPS: GhostPointFeatureProperties = {
  __portalFeatureProps: true,
  domain: Domain.EDITOR,
  type: EditorPropsType.GHOST_POINT,
};

export const POINT_BASE_PROPS: PointFeatureProperties = {
  __portalFeatureProps: true,
  domain: Domain.EDITOR,
  type: EditorPropsType.POINT,
};

export const INDEXED_POINT_BASE_PROPS: Omit<
  IndexedPointFeatureProperties,
  "index"
> = {
  __portalFeatureProps: true,
  domain: Domain.EDITOR,
  type: EditorPropsType.INDEXED_POINT,
};

export const DRAFT_POINT_BASE_PROPS: Pick<
  DraftPointFeatureProperties,
  keyof EditorBaseProps
> = {
  __portalFeatureProps: true,
  domain: Domain.EDITOR,
  type: EditorPropsType.DRAFT_POINT,
};
